import { useContext, useState } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { SignInResponse } from 'api/Identity/Types/SignInResponse'
import { TokenStorage } from 'api/tokenStorage'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { AzureAdContext, loginRequest } from 'lib/react-aad'
import { useAppDispatch } from 'store'
import { fetchFilterAction, fetchFilterTypesAction } from 'store/identity/identity.actions'
import { PREFERRED_DATE_FORMAT } from 'store/store'
import { getAndSetTicketsAssignedToUserSession } from 'store/tickets/tickets.actions'
import { AuthSessionContext } from 'utils/AuthContext'
import { useConfig } from 'utils/ConfigContext'
import { links } from 'utils/featureToggles'
import { sleep } from 'utils/sleep'

import { configFeatures } from '../../Config'
import { getErrorResponse, useApiClient } from '../../utils/api/ApiClient'
import { IdentityApiClient } from '../../utils/api/identityApi'
import { TicketsApiClient } from '../../utils/api/ticketsApi'
import { LoginForm, LoginFormValues } from './LoginForm'

const LoginPage = () => {
  const [isAddLoading, setAddLoading] = useState(false)
  const setToast = useContext(ToastContext)
  const msal = useContext(AzureAdContext)
  const config = useConfig()
  const navigate = useNavigate()
  const identityApiClient = useApiClient(IdentityApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)

  const location = useLocation()

  const dispatch = useAppDispatch()

  const [_, setAuth] = useContext(AuthSessionContext) // eslint-disable-line @typescript-eslint/no-unused-vars

  const onLogin = async (values: LoginFormValues) => {
    const user = await identityApiClient.signIn(values.email, values.password)
    setToken({
      accessToken: user.accessToken,
      refreshToken: user.refreshToken,
      sessionId: user.sessionId,
      dateFormatTypeName: user.dateFormatType.name,
      expires: user.expires,
    })
    await getPermissionsAndSetToken(user)
  }

  const onLoginWithAAD = async () => {
    setAddLoading(true)
    try {
      if (!msal) {
        throw new Error('MSAL instance is not initialized')
      }
      const authentication = await msal.loginPopup(loginRequest)
      const user = await identityApiClient.signInActiveDirectory(authentication.idToken)
      setToken({
        accessToken: user.accessToken,
        refreshToken: user.refreshToken,
        sessionId: user.sessionId,
        dateFormatTypeName: user.dateFormatType.name,
        expires: user.expires,
      })
      await getPermissionsAndSetToken(user)
    } catch (error) {
      console.log('error: ', error)
      const errorResponse = getErrorResponse(error)
      const errorCode = errorResponse?.code
      const errorMessage = errorResponse?.message
      if (errorCode === 'user_locked') {
        setToast(
          errorToast({
            title: 'Account Locked',
            subtitle: errorMessage || 'Your account has been locked. Please contact support.',
          })
        )
      } else {
        setToast(
          errorToast({
            title: errorCode || 'Login Failed',
            subtitle: errorMessage || 'An error occurred during SSO login',
          })
        )
      }
    } finally {
      setAddLoading(false)
    }
  }

  const setToken = (
    session: Partial<{
      accessToken: string | null
      refreshToken: string | null
      sessionId: string | null
      dateFormatTypeName: string | null
      expires: number | null
    }>
  ) => {
    if (
      session.refreshToken &&
      session.accessToken &&
      session.sessionId &&
      session.dateFormatTypeName &&
      session.expires
    ) {
      TokenStorage.storeRefreshToken(session.refreshToken, session.expires)
      TokenStorage.storeAccessToken(session.accessToken, session.expires)
      TokenStorage.storeSession(session.sessionId)
      localStorage.setItem(PREFERRED_DATE_FORMAT, session.dateFormatTypeName)
    }
  }

  const getPermissionsAndSetToken = async (user: SignInResponse) => {
    const permissions = await identityApiClient.getAuthUserPermission()
    setAuth({ user, permissions })
    await sleep(2000)

    if (user.forceUpdateCredentials) {
      navigate(links.profileSettings.url)
    } else {
      if (isLoginPage(location.pathname)) {
        navigate('/')
      }
    }
    await dispatch(getAndSetTicketsAssignedToUserSession(ticketsApiClient))
    await dispatch(fetchFilterAction(identityApiClient))
    await dispatch(fetchFilterTypesAction(identityApiClient))
    setToast(
      successToast({
        title: `You are signed in.`,
      })
    )
  }

  return (
    <LoginForm
      onSubmit={onLogin}
      onLoginWithAAD={onLoginWithAAD}
      isAddLoading={isAddLoading}
      isAzureActiveDirectoryEnabled={config.isFeatureEnabled(configFeatures.AzureActiveDirectory)}
    />
  )
}

const isLoginPage = (url: string) =>
  !!matchPath(
    {
      path: links.login.url,
    },
    url
  )

export default LoginPage
